<template>
  <v-container fluid v-if="loaded">
    <page-header>
      <v-breadcrumbs>
        <v-breadcrumbs-item to="/secure/courses/management"
          >Course Management</v-breadcrumbs-item
        >
        <v-breadcrumbs-divider>/</v-breadcrumbs-divider>
        <v-breadcrumbs-item
          :to="{
            name: 'CourseDetail',
            params: { id: $route.params.courseId, tab: 'tab-people' },
          }"
        >
          {{ course.name }}
        </v-breadcrumbs-item>
        <v-breadcrumbs-divider>/</v-breadcrumbs-divider>
        <v-breadcrumbs-item :disabled="true">
          {{
            selectedStudent
              ? `${selectedStudent.firstName} ${selectedStudent.lastName}`
              : "New Course Student"
          }}
        </v-breadcrumbs-item>
      </v-breadcrumbs>
    </page-header>

    <v-card width="100%" class="mx-auto">
      <v-card-title>{{ title || "Enroll Student" }}</v-card-title>
      <v-card-text class="mt-5">
        <v-form ref="form" lazy-validation>
        <v-row>
          <v-col cols="12" lg="6">
            <v-text-field
              v-model="startDate"
              label="Start Date*"
              prepend-icon="mdi-calendar"
              type="date"
              clearable
              :rules="[rules.required, rules.minDate(minDate, startDate)]"
              required
            ></v-text-field>
          </v-col>
          <v-col cols="12" lg="6">
            <v-text-field
              v-model="endDate"
              label="End Date"
              prepend-icon="mdi-calendar"
              type="date"
              clearable
              :rules="[rules.maxDate(maxDate, startDate), rules.dateRange(startDate, endDate)]"
            ></v-text-field>
          </v-col>
          <v-col cols="12">
            <v-autocomplete
              v-model="student"
              :items="studentSelects"
              :loading="isAppLoading"
              :search-input.sync="search"
              hide-no-data
              hide-selected
              no-filter
              clearable
              item-text="label"
              item-value="id"
              label="Student"
              placeholder="Enter name or email to search for student"
              prepend-icon="mdi-database-search"
              return-object
            ></v-autocomplete>
          </v-col>
          <v-col v-if="selectedStudent" cols="12">
            <v-list>
              <v-subheader>Selected Student</v-subheader>
              <v-list-item three-line>
                <v-list-item-content>
                  <v-list-item-title>
                    {{ selectedStudent.firstName }}
                    {{ selectedStudent.lastName }}
                    <span v-if="selectedStudent.grade">
                      (Grade {{ selectedStudent.grade }})</span
                    >
                  </v-list-item-title>
                  <v-list-item-subtitle>
                    {{
                      selectedStudent.center
                        ? selectedStudent.center.name
                        : "Center - N/A"
                    }}
                    /
                    {{ selectedStudent.email || "No Email" }}
                  </v-list-item-subtitle>
                  <v-list-item-subtitle>
                    {{ selectedStudent.about || "No Profile" }}
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-col>
          <v-col v-if="course.courseSchedules && course.courseSchedules.length > 0" cols="12">
              <v-subheader>Choose course schedules for student*</v-subheader>
              <v-simple-table class="mb-10">
                <tbody>
                  <tr
                    v-for="schedule in course.courseSchedules"
                    :key="schedule.id"
                  >
                    <td>
                      <v-checkbox v-model="schedule.selected" />
                    </td>
                    <td>
                      {{ schedule | scheduleText }}
                      <div v-if="schedule.remark">{{ schedule.remark }}</div>
                      <div v-if="schedule.selectedExcludeDates">
                        Exclude dates: {{ formatExcludeDates(schedule) }}
                      </div>
                    </td>
                    <td>
                      {{ schedule.startTime | formatTime }} -
                      {{ schedule.endTime | formatTime }}
                    </td>
                    <td style="text-align: right">
                      <v-menu offset-y>
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn icon v-bind="attrs" v-on="on">
                            <v-icon>mdi-dots-vertical</v-icon>
                          </v-btn>
                        </template>
                        <v-card
                          class="mx-auto"
                          max-width="400"
                          width="200"
                          tile
                        >
                          <v-list dense>
                            <v-list-item-group color="primary">
                              <v-list-item @click="excludeDates(schedule.id)">
                                <v-list-item-icon class="mr-2">
                                  <v-icon>mdi-calendar-remove</v-icon>
                                </v-list-item-icon>
                                <v-list-item-content>
                                  <v-list-item-title
                                    >Exclude Dates</v-list-item-title
                                  >
                                </v-list-item-content>
                              </v-list-item>
                            </v-list-item-group>
                          </v-list>
                        </v-card>
                      </v-menu>
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>
          </v-col>
          <v-col cols="12" v-else>
              There's no schedule defined for this course. Try adding one before
              adding teacher.
          </v-col>
          <v-col v-if="hasCalendarEvent" cols="12" lg="6">
            <v-checkbox
              v-model="addOnGoogleCalendar"
              value="true"
              label="Add to guest on Google Calendar"
            />
          </v-col>
        </v-row>
        </v-form>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn @click="addStudent">Enroll Student</v-btn>
        <v-spacer />
      </v-card-actions>
    </v-card>
  </v-container>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
const PageHeader = () => import("@/components/common/PageHeader.vue");
import moment from "moment";
import { rules } from "@/store/util/rules.js";

export default {
  props: ["value", "title"],
  data() {
    return {
      students: [],
      studentSelects: [],
      student: null,
      selectedStudent: null,
      loaded: false,
      search: null,
      course: {},
      addGoogleClassroom: false,
      addGoogleCalendar: true,
      isLoading: false,
      rules,
      form: null,
      startDate: null,
      endDate: null,
      minDate: null,
      maxDate: null
    };
  },
  filters: {
    formatTime(time) {
      return moment(time, "HH:mm").format("h:mm a");
    },
    formatDate(date) {
      return moment(date).format("ddd, MMM DD, yyyy");
    },
  },
  computed: {
    ...mapGetters(["isAppLoading"]),
    hasCalendarEvent() {
      if (this.course.courseSchedules) {
        const googleEventsCount = this.course.courseSchedules.filter(
          (cs) => cs.googleEventId != null
        ).length;
        return googleEventsCount > 0 ? true : false;
      } else {
        return false;
      }
    },
  },
  watch: {
    student(newstudent) {
      console.log("student changed: ");
      this.selectedStudent = this.students.find((t) => t.id == newstudent.id);
      console.log(this.selectedStudent);
    },
    search(query) {
      let selected = false;
      if (this.student) {
        selected = query == this.student.label;
      }

      if (!selected) {
        // Students have already been requested
        if (this.isLoading) return;

        this.queryStudents(query);
      }
    },
  },
  components: {
    PageHeader,
  },
  methods: {
    ...mapActions(["addErrorMessage"]),
    async initCourse() {
      const courseId = this.$route.params.courseId;
      if (!courseId) {
        return;
      }

      const result = await this.$axios
        .get("classroom/course", {
          params: { courseId },
        })
        .then((resp) => {
          this.course = resp.data;
          this.startDate = this.course.scheduleStart;
          this.endDate = this.course.scheduleEnd;
          this.minDate = this.startDate;
          this.maxDate = this.endDate;
        })
        .catch(() => {
          this.addErrorMessage("Error loading course from server");
        });

      return result;
    },
    queryStudents(term) {
      // cancel pending call
      clearTimeout(this._timerId);

      this.isLoading = true;

      // delay new call 500ms
      this._timerId = setTimeout(() => {
        this.studentSelects = []; //reset customer list
        const data = { searchTerm: term, searchEntities: ["STUDENT"] };
        this.$axios
          .post("search", data)
          .then((resp) => {
            const searchResults = resp.data;
            this.students = searchResults.students;

            if (this.students && this.students.length > 0) {
              this.studentSelects = this.students.map((s) => {
                return {
                  id: s.id,
                  label: `${s.firstName} ${s.lastName} (${s.center ? s.center.name+' / ':''} ${ s.email ? s.email : "No Email" })`,
                };
              });
            }
          })
          .catch((error) => {
            this.addErrorMessage(error.data.message || "Unknown Error");
          })
          .finally(() => (this.isLoading = false));
      }, 500);
    },
    addStudent() {
      if(!this.$refs.form.validate()){
        this.addMessage('Please check your input');
        return;
      }

      if (this.student) {
        const data = {
          studentId: this.student.id,
          courseId: this.course.id,
          inviteOnGoogleClassroom: this.addGoogleClassroom,
          addOnGoogleCalendar: this.addGoogleCalendar,
        };
        this.$axios
          .post("/course/student/update", data )
          .then(() => {
            this.$router.push({
              name: "CourseDetail",
              params: { id: this.course.id, tab: "tab-people" },
            });
          })
          .catch((error) => {
            this.addErrorMessage(error.data.message || "Unknown error");
          });
      } else {
        this.addErrorMessage("Please choose a student");
      }
    },
  },
  mounted() {
    this.initCourse().then(() => {
      this.course.courseSchedules.forEach( cs => {
        cs.selected = true;
        this.loaded = true;
      })
    });
  }
}
</script>

<style>
</style>